<template>
    <v-form ref="form" autocomplete="off" @submit.prevent="$emit('save')">
        <v-col cols="12" sm="6">
            <v-text-field
                    prepend-icon="account_box"
                    name="name"
                    type="text"
                    v-model="form.name"
                    :label="$t('admin.inviraRole.labels.name')"
                    :placeholder="$t('admin.inviraRole.labels.name')"
                    class="pa-3"
                    :rules="required"
                    :error="hasInputErrors('name')"
                    :error-messages="getInputErrors('name')"
                    required
            ></v-text-field>
        </v-col>

        <v-col cols="12" sm="6" v-if="!form.id">
            <v-select
                    v-model="form.companyId"
                    :items="companies"
                    :item-text="'razonSocial'"
                    :item-value="'id'"
                    :label="$t('admin.company.labels.razonSocial')"
                    :placeholder="$t('admin.company.labels.razonSocial')"
                    class="pa-3"
                    :rules="required"
                    :error="hasInputErrors('companyId')"
                    :error-messages="getInputErrors('companyId')"
                    :loading="loadingCompanies"
                    required
            ></v-select>
        </v-col>

        <v-col cols="12" sm="6">
            <v-select
                    v-model="form.childRoles"
                    :loading="loadingRoles"
                    :items="availableRoles"
                    :item-text="'name'"
                    :item-value="'id'"
                    attach
                    chips
                    :label="$t('admin.inviraRole.labels.childRoles')"
                    :placeholder="$t('admin.inviraRole.labels.childRoles')"
                    multiple
            ></v-select>
        </v-col>

        <v-col cols="12" v-if="permissionsList.length > 0">
            <v-row>
                <v-col cols="12" class="py-0" v-for="(permission,index) in permissionsList" :key="index">

                    <v-checkbox v-model="form.permissions"
                                :label="getPermissionsTranslation(permission)"
                                :value="permission"
                                @input="inputPermission(permission)"
                                dense hide-details
                    ></v-checkbox>
                </v-col>
            </v-row>
        </v-col>
    </v-form>
</template>

<script>
    import {InputErrorsByProps, RequiredRule} from '@dracul/common-frontend'
    import CompanyProvider from "../../../providers/CompanyProvider";
    import InviraRoleProvider from "../../../providers/InviraRoleProvider";

    export default {
        name: "InviraRolesForm",
        mixins: [InputErrorsByProps, RequiredRule],
        props: {
            value: {
                type: Object,
                required: true
            },
            permissions: {
                type: Array,
                default: () => null
            },
            roles: {
                type: Array,
                default: () => null
            }
        },
        data() {
            return {
                loadingRoles: false,
                loadingCompanies: false,
                loadingPermissions: false,
                companies: [],
                loadedRoles: [],
                loadedPermissions: []
            }
        },
        created() {
            this.loadCompanies();
            if (!this.permissions) {
                this.loadPermissions();
            }
            if (!this.roles) {
                this.loadRoles();
            }
        },
        computed: {
            form: {
                get() {
                    return this.value
                },
                set(val) {
                    this.$emit('input', val)
                }
            },
            hasPermission() {
                return (permission) => {
                    return this.form.permissions && Array.isArray(this.form.permissions) && this.form.permissions.includes(permission)
                }
            },
            availableRoles() {
                const rolesToUse = this.roles || this.loadedRoles;
                // Filter out the current role if editing (to prevent circular references)
                if (this.form.id && rolesToUse) {
                    return rolesToUse.filter(r => r.id !== this.form.id);
                }
                return rolesToUse || [];
            },
            permissionsList() {
                return this.permissions || this.loadedPermissions;
            }
        },
        watch: {
            form: {
                handler(newVal) {
                    this.$emit('input', newVal)
                },
                deep: true
            }
        },
        methods: {
            loadCompanies() {
                this.loadingCompanies = true;
                CompanyProvider.fetchCompanies()
                    .then(r => {
                        this.companies = r.data.companyFetch || [];
                    })
                    .catch(err => {
                        console.error('Error loading companies:', err);
                    })
                    .finally(() => {
                        this.loadingCompanies = false;
                    });
            },
            loadPermissions() {
                this.loadingPermissions = true;
                InviraRoleProvider.fetchRoles()
                    .then(r => {
                        const allRoles = r.data.adminInviraRoleFetch || [];
                        // Extract all unique permissions from all roles
                        const allPermissions = new Set();
                        allRoles.forEach(role => {
                            if (role.permissions && Array.isArray(role.permissions)) {
                                role.permissions.forEach(perm => allPermissions.add(perm));
                            }
                        });
                        this.loadedPermissions = Array.from(allPermissions).sort();
                    })
                    .catch(err => {
                        console.error('Error loading permissions:', err);
                    })
                    .finally(() => {
                        this.loadingPermissions = false;
                    });
            },
            loadRoles() {
                this.loadingRoles = true;
                InviraRoleProvider.fetchRoles()
                    .then(r => {
                        this.loadedRoles = r.data.adminInviraRoleFetch || [];
                    })
                    .catch(err => {
                        console.error('Error loading roles:', err);
                    })
                    .finally(() => {
                        this.loadingRoles = false;
                    });
            },
            validate() {
                return this.$refs.form.validate()
            },
            getPermissionsTranslation(permission) {
                if (this.$te('role.permissions.' + permission)) {
                    return this.$t('role.permissions.' + permission)
                } else if (this.$te('admin.inviraRole.permissions.' + permission)) {
                    return this.$t('admin.inviraRole.permissions.' + permission)
                } else {
                    return permission
                }
            },
            inputPermission(permission) {
                // Ensure permissions is always an array
                if (!this.form.permissions || !Array.isArray(this.form.permissions)) {
                    this.$set(this.form, 'permissions', []);
                }
                if (this.hasPermission(permission)) {
                    this.form.permissions = this.form.permissions.filter(p => p != permission)
                } else {
                    this.form.permissions.push(permission)
                }
            },
        }
    }
</script>

<style scoped>

</style>
